import type { EventRow } from './types';
import { dartRound } from './types';

/**
 * 추세 2 + 임계 배너 2.
 *
 * ⚠️⚠️ **일별 추세에는 표본 하한이 있어야 한다.** 분모 0만 거르면 부족하다 —
 * 기간 창의 **첫날은 대개 조각**이라 회차가 한둘뿐이고, 그러면 완주율이 0%
 * 아니면 100% 밖에 안 나온다. ★ 실측에서 그 0% 가 스파크라인 왼쪽을 눌러
 * 가짜 급락을 그렸고 변화량을 **「50.0%p 개선」**으로 뒤집었다.
 */

/** `YYYY-MM-DD`(지역 시각). ★ Dart 쪽과 같은 산식이어야 한다. */
function dayKey(d: Date): string {
  return (
    `${d.getFullYear()}-` +
    `${String(d.getMonth() + 1).padStart(2, '0')}-` +
    `${String(d.getDate()).padStart(2, '0')}`
  );
}

/**
 * 최근 7일 일별 추세. 쉼표로 이어 붙인 문자열이다.
 *
 * ⚠️ 커스텀 위젯에 리스트를 넘기지 않는 것이 이 프로젝트의 규약이라 문자열이다
 * (`LensSparkline`). 이식 뒤에도 **정답지가 이 모양**이라 그대로 둔다.
 *
 * ★ `-1` 은 「그날은 판단 보류」다. 소비자(스파크라인 · `lensTrendDelta`)가
 *   전부 `-1` 을 건너뛰므로 **하한을 한 곳에서만 고치면 된다.**
 */
export function lensDailyTrend(
  events?: EventRow[] | null,
  metric?: string | null,
  _costPerTurnKrw?: number | null,
  usdKrwRate?: number | null,
  costFeeRate?: number | null,
): string {
  const rows = events ?? [];
  if (rows.length === 0) return '';

  const days = new Set<string>();
  const started = new Map<string, Set<string>>();
  const ended = new Map<string, Set<string>>();
  const attempts = new Map<string, number>();
  const blocked = new Map<string, number>();
  const unread = new Map<string, number>();
  const turns = new Map<string, number>();
  const failed = new Map<string, number>();
  const usdByDay = new Map<string, number>();

  const addTo = (m: Map<string, Set<string>>, k: string, v: string): void => {
    let s = m.get(k);
    if (s === undefined) { s = new Set(); m.set(k, s); }
    s.add(v);
  };

  for (const e of rows) {
    const at = e.occurredAt;
    if (at === null) continue;
    const k = dayKey(at);
    days.add(k);
    if (e.costUsd > 0) usdByDay.set(k, (usdByDay.get(k) ?? 0) + e.costUsd);
    const n = e.eventName;
    if (n === 'run_started') {
      addTo(started, k, e.runId);
    } else if (n === 'ending_reached') {
      addTo(ended, k, e.runId);
    } else if (n === 'action_interpreted') {
      attempts.set(k, (attempts.get(k) ?? 0) + 1);
      turns.set(k, (turns.get(k) ?? 0) + 1);
      if (e.parseOk === false) blocked.set(k, (blocked.get(k) ?? 0) + 1);
    } else if (n === 'input_blocked' || n === 'unsupported_action_requested') {
      attempts.set(k, (attempts.get(k) ?? 0) + 1);
      blocked.set(k, (blocked.get(k) ?? 0) + 1);
      if (e.judgmentKind === 'reject_unclear' || e.judgmentKind === 'aside') {
        unread.set(k, (unread.get(k) ?? 0) + 1);
      }
    } else if (n === 'narration_validation_failed') {
      failed.set(k, (failed.get(k) ?? 0) + 1);
    }
  }

  const keys = [...days].sort();
  const recent = keys.length <= 7 ? keys : keys.slice(keys.length - 7);
  const kind = metric ?? '';
  const krwPerUsd = (usdKrwRate ?? 0) * (1 + (costFeeRate ?? 0) / 100);

  // ★ 회차 분모는 3, 턴 분모는 20 미만이면 `-1`(판단 보류)이다.
  const minRuns = 3;
  const minTurns = 20;

  // ⚠️ 발행 규격이 바뀐 경계일은 통째로 보류한다.
  const boundaryDay = dayKey(new Date(Date.UTC(2026, 8, 19, 17, 13, 51)));

  const out: string[] = [];
  for (const k of recent) {
    let v: number;
    if (kind === 'completion') {
      const s = started.get(k)?.size ?? 0;
      v = k === boundaryDay ? -1 : s < minRuns ? -1 : ((ended.get(k)?.size ?? 0) * 100) / s;
    } else if (kind === 'blocked') {
      const a = attempts.get(k) ?? 0;
      v = a < minTurns ? -1 : ((blocked.get(k) ?? 0) * 100) / a;
    } else if (kind === 'unread') {
      const a = attempts.get(k) ?? 0;
      v = a < minTurns ? -1 : ((unread.get(k) ?? 0) * 100) / a;
    } else if (kind === 'narration') {
      const t = turns.get(k) ?? 0;
      v = t < minTurns ? -1 : ((failed.get(k) ?? 0) * 100) / t;
    } else {
      const s = started.get(k)?.size ?? 0;
      v = s < minRuns || (usdByDay.get(k) ?? 0) <= 0 ? -1 : (usdByDay.get(k)! * krwPerUsd) / s;
    }
    out.push(v.toFixed(2));
  }
  return out.join(',');
}

/**
 * 추세 변화량.
 *
 * ★★ **양 끝점을 빼지 않는다** — 하루치가 크게 흔들려 끝점 하나가 판정을
 * 뒤집는다. 앞 절반 평균과 뒤 절반 평균을 견주고, **홀수면 가운데 날을 양쪽에
 * 넣는다**(어느 쪽에도 안 넣으면 3점일 때 가운데가 통째로 버려진다).
 *
 * ★ 색을 데이터에 바인딩할 수 없어 **글자로** 쓰는데, 덕분에 색 없이도 방향이
 *   읽힌다. ⚠️ 추세 문자열을 **다시 집계하지 않고** 스파크라인과 같은 것을
 *   읽는다 — 두 곳이 각자 집계하면 언젠가 어긋나고 그때 어느 쪽이 맞는지
 *   알 수 없다.
 */
export function lensTrendDelta(
  series?: string | null,
  krw?: boolean | null,
  higherIsBetter?: boolean | null,
): string {
  const raw = (series ?? '').trim();
  if (raw === '') return '';
  const xs: number[] = [];
  for (const p of raw.split(',')) {
    const v = Number.parseFloat(p.trim());
    // ★ `-1`(판단 보류)을 건너뛴다.
    if (!Number.isNaN(v) && v >= 0) xs.push(v);
  }
  if (xs.length < 2) return '';
  const half = Math.ceil(xs.length / 2);
  const mean = (ys: number[]): number => ys.reduce((a, b) => a + b, 0) / ys.length;
  const diff = mean(xs.slice(xs.length - half)) - mean(xs.slice(0, half));
  const mag = Math.abs(diff);
  const text = (krw ?? false) ? String(dartRound(mag)) : mag.toFixed(1);
  if (text === '0' || text === '0.0') return '변화 없음';
  const unit = (krw ?? false) ? '원' : '%p';
  const better = (higherIsBetter ?? false) ? diff > 0 : diff < 0;
  return `${text}${unit} ${better ? '개선' : '악화'}`;
}

/**
 * 임계 배너.
 *
 * ⚠️⚠️ **원가만 판정한다**(OBXYD 회신 25, 2026-09-20). 나머지 셋은 기획서가 값을
 * 주기는 하지만 **세는 법이 한 줄도 안 적혀 있고** 둘은 서버가 잴 수단 자체가
 * 없다. ★★ **그 값이 내 분자·분모의 값인지 알 수 없으면 판정이 무의미하다** —
 * 진행 불가는 46%대라 1% 기준이면 **늘 빨강**이고, 늘 빨간 불은 정보가 0 이다.
 *
 * ★ 값과 변화량은 색 없이도 읽힌다. 근거가 서면 되살린다.
 */
export function lensThresholdAlertV2(
  events?: EventRow[] | null,
  _completionRate?: number | null,
  _targetCompletion?: number | null,
  _blockedRate?: number | null,
  _maxBlocked?: number | null,
  _narrationRate?: number | null,
  _maxNarration?: number | null,
  costPerRun?: number | null,
  costWarn?: number | null,
): string {
  // ⚠️ 표본이 비면 0% 를 위반으로 읽으면 안 된다.
  if ((events ?? []).length === 0) {
    // ★ 「기간을 넓혀 보세요」만 쓰면 안 된다 — 기간이 이미 「전체」인데 비어
    //   있는 경우가 있고, 그때 그 문장은 **할 수 없는 일을 시킨다.**
    return (
      '이벤트가 없습니다. 기간이 좁으면 넓히고, 그래도 비어 있으면 ' +
      '설정에서 데이터 소스와 "제외할 회차" 접두를 확인하세요.'
    );
  }

  const issues: string[] = [];
  const missing: string[] = [];

  const check = (
    label: string,
    value: number | null | undefined,
    limit: number | null | undefined,
    higherIsBetter: boolean,
    message: (v: number, l: number) => string,
  ): void => {
    const v = value ?? -1;
    // ★ 표본 없음(-1)은 위반이 아니다. 따로 센다.
    if (v < 0) { missing.push(label); return; }
    const l = limit ?? 0;
    const violated = higherIsBetter ? v < l : v > l;
    if (violated) issues.push(message(v, l));
  };

  check('회차당 원가', costPerRun, costWarn, false,
    (v, l) => `회차당 원가 ${dartRound(v)}원 (경고 ${dartRound(l)}원 초과)`);

  const note = missing.length === 0
    ? ''
    : `  ·  집계할 표본이 없는 축 ${missing.length}개: ${missing.join(', ')}`;

  if (issues.length === 0) {
    // 위반이 없어도 미집계 축이 있으면 그 사실을 **반드시 말한다.**
    if (missing.length === 0) return '';
    return `회차당 원가는 경고선 안입니다.${note}`;
  }
  return `기준선 위반 ${issues.length}건 — ${issues.join('  ·  ')}${note}`;
}

/** 오늘의 진단 — 가장 심한 위반 하나 + 가장 큰 낙차. */
export function lensTodayDiagnosis(
  events?: EventRow[] | null,
  _completionRate?: number | null,
  _targetCompletion?: number | null,
  _blockedRate?: number | null,
  _maxBlocked?: number | null,
  _narrationRate?: number | null,
  _maxNarration?: number | null,
  costPerRun?: number | null,
  costWarn?: number | null,
): string {
  const rows = events ?? [];
  if (rows.length === 0) return '집계할 표본이 없습니다.';

  const entered = new Map<string, Set<string>>();
  for (const e of rows) {
    if (e.eventName !== 'scene_entered') continue;
    if (e.sceneId === '' || e.runId === '') continue;
    let s = entered.get(e.sceneId);
    if (s === undefined) { s = new Set(); entered.set(e.sceneId, s); }
    s.add(e.runId);
  }
  let dropClause = '';
  if (entered.size >= 2) {
    const keys = [...entered.keys()].sort();
    const counts = keys.map((k) => entered.get(k)!.size);
    let at = -1;
    let most = 0;
    for (let i = 1; i < counts.length; i++) {
      const d = counts[i - 1]! - counts[i]!;
      if (d > most) { most = d; at = i; }
    }
    if (at > 0 && most > 0) {
      const pct = ((most * 100) / counts[at - 1]!).toFixed(1);
      dropClause =
        ` 가장 많은 회차가 ${keys[at - 1]} 에서 빠져나갔습니다 — ` +
        `${counts[at - 1]}회차 중 ${most}회차(${pct}%)가 다음 장면` +
        `(${keys[at]})에 도달하지 못했습니다.`;
    }
  }

  let worst = '';
  let worstText = '';
  let worstScore = 0;
  const weigh = (name: string, v: number, limit: number, text: string, higherBetter: boolean): void => {
    if (v < 0 || limit <= 0) return;
    const score = higherBetter ? (limit - v) / limit : (v - limit) / limit;
    if (score <= 0) return;
    if (score > worstScore) { worstScore = score; worst = name; worstText = text; }
  };

  const k = costPerRun ?? -1;
  weigh(
    '회차당 원가',
    k,
    costWarn ?? 0,
    `회차당 원가가 ${dartRound(k)}원으로 경고 기준(${dartRound(costWarn ?? 0)}원)을 넘었습니다.`,
    false,
  );

  if (worst === '') {
    return dropClause === '' ? '회차당 원가는 경고선 안입니다.' : dropClause.trim();
  }
  return `${worstText}${dropClause}`;
}
